import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Home } from "lucide-react";

const TableOfContents = ({
	content,
	pageName,
	accentColor,
	darkMode = false,
}) => {
	const [headings, setHeadings] = useState([]);
	const [activeHeading, setActiveHeading] = useState("");
	const [scrollProgress, setScrollProgress] = useState(0);
	const tocRef = useRef(null);

	useEffect(() => {
		// Parse markdown content to extract headings
		const parseHeadings = (markdownContent) => {
			if (!markdownContent) return [];

			const lines = markdownContent.split("\n");
			const extractedHeadings = [];

			lines.forEach((line) => {
				const trimmedLine = line.trim();
				if (trimmedLine.startsWith("#")) {
					const level = trimmedLine.match(/^#+/)[0].length;
					if (level <= 3) {
						// Only h1, h2, h3
						const text = trimmedLine.replace(/^#+\s*/, "");
						const id = text
							.toLowerCase()
							.replace(/[^a-z0-9]+/g, "-")
							.replace(/(^-|-$)/g, "");
						extractedHeadings.push({
							level,
							text,
							id,
						});
					}
				}
			});

			return extractedHeadings;
		};

		// Combine all content sections and parse headings
		const allContent = Array.isArray(content)
			? content.join("\n")
			: content || "";
		const parsedHeadings = parseHeadings(allContent);
		setHeadings(parsedHeadings);
	}, [content]);

	useEffect(() => {
		// Handle scroll progress and active heading with more frequent updates
		const handleScroll = () => {
			const scrollTop = window.scrollY;
			const docHeight =
				document.documentElement.scrollHeight - window.innerHeight;
			const progress =
				docHeight > 0 ? Math.min((scrollTop / docHeight) * 100, 100) : 0;
			setScrollProgress(progress);

			// Find active heading based on scroll position
			if (headings.length > 0) {
				const headingElements = headings
					.map((h) => document.getElementById(h.id))
					.filter(Boolean);
				let activeId = "";

				for (let i = headingElements.length - 1; i >= 0; i--) {
					const element = headingElements[i];
					if (element && element.getBoundingClientRect().top <= 100) {
						activeId = element.id;
						break;
					}
				}

				setActiveHeading(activeId);
			}
		};

		// Use requestAnimationFrame for smoother updates
		const smoothHandleScroll = () => {
			requestAnimationFrame(handleScroll);
		};

		window.addEventListener("scroll", smoothHandleScroll, { passive: true });
		handleScroll(); // Initial call

		return () => window.removeEventListener("scroll", smoothHandleScroll);
	}, [headings]);

	const scrollToHeading = (id) => {
		const element = document.getElementById(id);
		if (element) {
			element.scrollIntoView({ behavior: "smooth", block: "start" });
		}
	};

	// Calculate TOC height based on content
	const tocHeight = Math.max(
		200, // minimum height
		headings.length * 32 + 20 // height for headings + space for page name and home button
	);

	return (
		<div className="fixed left-4 top-1/2 transform -translate-y-1/2 z-40 hidden 2xl-custom:block">
			<div className="flex" ref={tocRef}>
				{/* Progress bar with dots */}
				<div
					className="w-1 bg-gray-200 relative mr-4 rounded-full overflow-hidden flex flex-col justify-between"
					style={{ height: `${tocHeight}px` }}
				>
					{/* Starting dot - always visible */}
					<div
						className="w-2 h-2 rounded-full absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-10"
						style={{ backgroundColor: accentColor }}
					/>

					{/* Progress fill */}
					<div
						className="w-full transition-all duration-75 ease-out rounded-full"
						style={{
							height: `${Math.max(4, scrollProgress) - 1}%`,
							backgroundColor: accentColor,
						}}
					/>

					{/* Ending dot */}
					<div
						className={`w-4 h-4 rounded-full absolute bottom-0 left-1/2 transform -translate-x-1/2 translate-y-1/2 z-10`}
						style={{ backgroundColor: accentColor }}
					/>
				</div>

				{/* Table of contents */}
				<div
					className="flex flex-col justify-between max-w-[200px]"
					style={{ height: `${tocHeight}px` }}
				>
					{/* Page name */}
					<div className="mb-4">
						<h3 className="text-sm font-semibold transition-colors">
							{pageName}
						</h3>
					</div>

					{/* Headings */}
					{headings.length > 0 && (
						<div className="flex flex-col flex-grow">
							{headings.map((heading, index) => (
								<button
									key={index}
									onClick={() => scrollToHeading(heading.id)}
									className={`rounded-md text-left text-sm transition-colors duration-200 px-2 py-1 ${
										activeHeading === heading.id
											? "font-medium bg-[#EBEBEB]"
											: "text-gray-500"
									} ${
										heading.level === 1
											? ""
											: heading.level === 2
											? "ml-2"
											: "ml-4 text-xs"
									}`}
								>
									{heading.text}
								</button>
							))}
						</div>
					)}

					{/* Home button */}
					<div className="mt-4">
						<Link
							to="/"
							className="flex items-center font-semibold space-x-2 text-gray-500 transition-colors duration-200 text-sm"
						>
							<Home size={14} />
							<span>Home</span>
						</Link>
					</div>
				</div>
			</div>
		</div>
	);
};

export default TableOfContents;
